import { Button, Input, Modal } from '@components/common';
import { DropdownSelect } from '@components/common/DropdownSelect';
import React, { useEffect, useState } from 'react';
import {
  clearEbmHostedApiKey,
  OutletEbmConfig,
  setEbmHostedApiKey,
  upsertEbmConfig,
} from '@/renderer/services/ebmConfigService';
import { apiError, apiSuccess } from '@/renderer/util/notify';

interface EbmSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  outletId: string;
  existingConfig: OutletEbmConfig | null;
  hasKey: boolean;
  onSaved: (config: OutletEbmConfig, keyResult?: { hasKey: boolean }) => void;
}

interface FormState {
  deployment_mode: 'LOCAL_VSDC' | 'HOSTED';
  vsdc_base_url: string;
  tin: string;
  bhf_id: string;
  dvc_srl_no: string;
  hosted_api_key: string;
  pos_mrc_code: string;
}

const EMPTY_FORM: FormState = {
  deployment_mode: 'LOCAL_VSDC',
  vsdc_base_url: '',
  tin: '',
  bhf_id: '00',
  dvc_srl_no: '',
  hosted_api_key: '',
  pos_mrc_code: '',
};

export const EbmSetupModal: React.FC<EbmSetupModalProps> = ({
  isOpen,
  onClose,
  outletId,
  existingConfig,
  hasKey,
  onSaved,
}) => {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [localHasKey, setLocalHasKey] = useState(hasKey);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(
      existingConfig
        ? {
            deployment_mode: existingConfig.deployment_mode,
            vsdc_base_url: existingConfig.vsdc_base_url ?? '',
            tin: existingConfig.tin ?? '',
            bhf_id: existingConfig.bhf_id || '00',
            dvc_srl_no: existingConfig.dvc_srl_no ?? '',
            hosted_api_key: '',
            pos_mrc_code: existingConfig.pos_mrc_code ?? '',
          }
        : EMPTY_FORM,
    );
    setLocalHasKey(hasKey);
  }, [isOpen, existingConfig, hasKey]);

  const handleClearKey = async () => {
    const response = await clearEbmHostedApiKey(outletId);
    if (response.success) {
      setLocalHasKey(false);
      apiSuccess('Hosted API key cleared.');
    } else {
      apiError(response.error || 'Failed to clear key');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const saved = await upsertEbmConfig({
        outlet_id: outletId,
        deployment_mode: form.deployment_mode,
        vsdc_base_url: form.vsdc_base_url.trim() || null,
        tin: form.tin.trim() || null,
        bhf_id: form.bhf_id.trim() || '00',
        // Deliberately NOT trimmed — RRA compares this byte-exact, and some
        // real RRA-issued device serials include a meaningful leading or
        // trailing space that is part of the actual credential.
        dvc_srl_no: form.dvc_srl_no || null,
        pos_mrc_code: form.pos_mrc_code.trim() || null,
      });

      let keyResult: { hasKey: boolean } | undefined;
      if (form.deployment_mode === 'HOSTED' && form.hosted_api_key.trim()) {
        const keyResponse = await setEbmHostedApiKey(
          outletId,
          form.hosted_api_key.trim(),
        );
        if (!keyResponse.success) {
          apiError(keyResponse.error || 'Failed to store hosted API key');
        } else {
          keyResult = { hasKey: true };
        }
      }

      apiSuccess('EBM configuration saved.');
      onSaved(saved, keyResult);
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={existingConfig ? 'Edit EBM Device Setup' : 'Set Up EBM Device'}
      size="md"
    >
      <div className="space-y-4 text-sm text-gray-900">
        <DropdownSelect
          label="Deployment Mode"
          value={form.deployment_mode}
          onChange={(value) =>
            setForm((prev) => ({
              ...prev,
              deployment_mode: value as 'LOCAL_VSDC' | 'HOSTED',
            }))
          }
          options={[
            { value: 'LOCAL_VSDC', label: 'Local VSDC (on-site device)' },
            { value: 'HOSTED', label: 'Hosted (remote EBM API)' },
          ]}
        />

        {form.deployment_mode === 'LOCAL_VSDC' ? (
          <Input
            label="VSDC Base URL"
            placeholder="http://192.168.1.71:8080/rra"
            value={form.vsdc_base_url}
            onChange={(value) =>
              setForm((prev) => ({ ...prev, vsdc_base_url: value }))
            }
            helperText="The local RRA VSDC container's base URL, reachable on this outlet's network."
          />
        ) : (
          <div className="space-y-2">
            <Input
              label="Hosted API Key"
              type="password"
              placeholder={localHasKey ? '••••••••••••' : 'Enter API key'}
              value={form.hosted_api_key}
              onChange={(value) =>
                setForm((prev) => ({ ...prev, hosted_api_key: value }))
              }
              helperText={
                localHasKey
                  ? 'Key configured — leave blank to keep it, or enter a new value to replace it.'
                  : 'No key set — required before this outlet can activate in HOSTED mode.'
              }
            />
            {localHasKey && (
              <button
                type="button"
                onClick={handleClearKey}
                className="text-xs text-red-600 underline"
              >
                Clear key
              </button>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="TIN"
            value={form.tin}
            onChange={(value) => setForm((prev) => ({ ...prev, tin: value }))}
          />
          <Input
            label="BHF ID"
            value={form.bhf_id}
            onChange={(value) =>
              setForm((prev) => ({ ...prev, bhf_id: value }))
            }
          />
        </div>
        <Input
          label="Device Serial Number"
          value={form.dvc_srl_no}
          onChange={(value) =>
            setForm((prev) => ({ ...prev, dvc_srl_no: value }))
          }
          helperText="Enter exactly as issued by RRA, including any leading or trailing spaces — RRA compares this value byte-for-byte."
        />
        <Input
          label="POS MRC Code (optional)"
          value={form.pos_mrc_code}
          onChange={(value) =>
            setForm((prev) => ({ ...prev, pos_mrc_code: value }))
          }
          helperText="The Machine Registration Code RRA issues to smartPOS Cloud itself — leave blank until RRA has issued one. Shown on printed fiscal receipts."
        />

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} isLoading={isSaving}>
            Save configuration
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EbmSetupModal;
